<script setup lang="ts">
import LoadingModal from "@/components/LoadingModal.vue";
import RoundedBox from "@/components/RoundedBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledInput from "@/components/StyledInput.vue";
import StyledSelect from "@/components/StyledSelect.vue";
import StyledSwitch from "@/components/StyledSwitch.vue";
import TransitionWrapper from "@/components/TransitionWrapper.vue";
import useCredit from "@/composables/useCredit";
import FieldSet from "@/modules/companies/components/FieldSet.vue";
import useCompanyCredit from "@/modules/companies/composables/useCompanyCredit";
import { CompanyEditTab } from "@/modules/companies/interfaces/companyEditTab";
import { InformationCircleIcon } from "@heroicons/vue/24/solid";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, inject, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

interface Props extends CompanyEditTab {}

const props = defineProps<Props>();

const toast = useToast();
const error = ref("");

const route = useRoute();

const {
  deleteCompanyCreditAsync,
  updateCompanyCreditAsync,
  updateCompanyCreditLoading,
  restoreCompanyCreditAsync,
  restoreCompanyCreditLoading,
  deleteCompanyCreditLoading,
} = useCredit();

const {
  createCompanyCreditMutation,
  createCompanyCreditLoading,
  credit,
  isLoading,
  refetchCompanyCredit,
} = useCompanyCredit({
  params: {
    companyId: Number(route.params.id),
  },
  immediate: true,
});

const queryClient = useQueryClient();

const { invalidateQueryData } = inject("editCompanyProvides") as {
  invalidateQueryData: () => Promise<void>;
};

// Función para invalidar todas las queries relacionadas
const invalidateAllCreditQueries = async () => {
  await Promise.all([
    refetchCompanyCredit(),
    queryClient.invalidateQueries(["companyCredit", Number(route.params.id)]),
    queryClient.invalidateQueries(["company", props.company.id]),
    invalidateQueryData(),
  ]);
};

const creditFields = ref({
  legalRepresentativeName: credit.value?.legalRepresentativeName,
  legalRepresentativeEmail: credit.value?.legalRepresentativeEmail,
  available: credit.value?.available,
  limit: credit.value?.limit,
});

const creditSelects = ref({
  deadline: credit.value?.deadline,
});

const enabled = ref(Boolean(credit.value));

const fieldInfo = {
  legalRepresentativeName: ["Representante legal:", "ej. Juan Pérez"],
  legalRepresentativeEmail: [
    "Correo electrónico:",
    "ej. manuel.mtzv816@gmail.com",
  ],
  limit: ["Límite de crédito:", "ej. 100,000"],
  available: ["Crédito disponible:", "ej. 50,000"],
};

const termsOfCredit = [
  { label: "5 días", value: 5 },
  { label: "10 días", value: 10 },
  { label: "15 días", value: 15 },
  { label: "30 días", value: 30 },
  { label: "45 días", value: 45 },
];

const rules = {
  legalRepresentativeName: {
    required: helpers.withMessage(
      "El nombre de representante legal es obligatorio",
      required,
    ),
  },
  legalRepresentativeEmail: {
    required: helpers.withMessage(
      "El correo electrónico del representante legal es obligatorio",
      required,
    ),
    requiredIf: helpers.withMessage(
      "El correo electrónico no es válido",
      (value: any) => {
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        return emailRegex.test(value);
      },
    ),
  },
  limit: {
    required: helpers.withMessage(
      "El límite de crédito es obligatorio",
      required,
    ),
  },

  available: {
    required: helpers.withMessage(
      "El crédito disponible es obligatorio",
      required,
    ),
    positive: helpers.withMessage(
      "El crédito disponible debe ser un número positivo",
      (value: number) => value > 0,
    ),
    notExceedLimit: helpers.withMessage(
      "El crédito disponible no puede ser mayor que el límite de crédito",
      (value: number) => {
        const limit = Number(creditFields.value.limit) || 0; // Convert to number or default to 0
        return value <= limit;
      },
    ),
  },
};

const v$ = useVuelidate(rules, creditFields);

const handleCreate = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  try {
    await createCompanyCreditMutation({
      params: {
        limit: creditFields.value.limit ?? "",
        available: creditFields.value.available ?? "",
        legalRepresentativeName:
          creditFields.value.legalRepresentativeName ?? "",
        legalRepresentativeEmail:
          creditFields.value.legalRepresentativeEmail ?? "",
        deadline: creditSelects.value.deadline ?? 0,
        companyId: props.company.id,
      },
      config: {
        raiseToast: true,
        onSuccessToast: "Activando crédito correctamente",
        onErrorToast: "",
      },
    });

    await invalidateAllCreditQueries();
  } catch (err) {
    toast.error("Error al crear el crédito");
  }
};
const handleUpdate = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  try {
    await updateCompanyCreditAsync({
      params: {
        creditId: credit.value?.id,
        legalRepresentativeName: creditFields.value.legalRepresentativeName,
        legalRepresentativeEmail: creditFields.value.legalRepresentativeEmail,
        deadline: Number(creditSelects.value.deadline),
        limit: creditFields.value.limit,
        available: creditFields.value.available,
      },
      config: {
        raiseToast: true,
        onSuccessToast: "Crédito actualizado correctamente",
        onErrorToast: "",
      },
    });
  } catch (err) {
    toast.error("Error al actualizar el crédito");
  } finally {
    await invalidateAllCreditQueries();
  }
};

const handleRestore = async () => {
  try {
    await restoreCompanyCreditAsync({
      params: {
        id: credit.value?.id!,
      },
      config: {
        raiseToast: true,
        onSuccessToast: "Crédito restaurado correctamente",
        onErrorToast: "",
      },
    });
  } catch (err) {
    toast.error("Error al restaurar el crédito");
  } finally {
    await invalidateAllCreditQueries();
  }
};

const handleDelete = async () => {
  try {
    await deleteCompanyCreditAsync({
      params: {
        id: credit.value?.id!,
      },
      config: {
        raiseToast: true,
        onSuccessToast: "Crédito desactivado correctamente",
        onErrorToast: "",
      },
    });
  } catch (err) {
    toast.error("Error al desactivar el crédito");
  } finally {
    await invalidateAllCreditQueries();
  }
};

type creditFieldsKey = keyof typeof creditFields.value;
type fieldInfoKey = keyof typeof fieldInfo;

const checkCreditIsActive = computed(() => {
  if (!credit.value) {
    return "No cuentas con crédito empresarial habilitalo";
  } else {
    return "El credito de esta empresa se encuentra deshabilitado.";
  }
});

watch(
  () => credit.value,
  (value) => {
    if (value) {
      enabled.value = !value.deleted;

      creditFields.value = {
        legalRepresentativeName: value.legalRepresentativeName,
        legalRepresentativeEmail: value.legalRepresentativeEmail,
        limit: value.limit,
        available: value.available,
      };
      creditSelects.value = {
        deadline: value.deadline,
      };
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <RoundedBox class="bg-white" v-if="!isLoading">
    <section class="w-full grid gap-[1.563rem]">
      <div class="flex items-center justify-between gap-4">
        <h2 class="text-xl font-medium">Crédito empresarial</h2>
        <div>
          <StyledButton
            label="Nuevo crédito"
            class="bg-[#253C70] text-white ml-auto"
            v-if="!credit"
            @click="() => (enabled = !enabled)"
          />

          <div v-if="credit">
            <StyledSwitch
              v-if="credit.deleted"
              v-model:enabled="enabled"
              v-tooltip="'Reactivar crédito'"
              @update:enabled="handleRestore"
              :disabled="restoreCompanyCreditLoading"
            />

            <StyledSwitch
              v-if="!credit.deleted"
              v-model:enabled="enabled"
              v-tooltip="'Desactivar Crédito'"
              @update:enabled="handleDelete"
              :disabled="deleteCompanyCreditLoading"
            />
          </div>
        </div>
      </div>

      <template v-if="enabled">
        <FieldSet>
          <StyledInput
            :is-required="true"
            v-for="key in Object.keys(creditFields)"
            :key="key"
            :error="error"
            :label="fieldInfo[key as fieldInfoKey].at(0)"
            :placeholder="fieldInfo[key as fieldInfoKey].at(1)"
            v-model="creditFields[key as creditFieldsKey]"
          />

          <StyledSelect
            :is-required="true"
            label="Plazo de crédito"
            :error="error"
            placeholder="Seleccione un plazo"
            :options="termsOfCredit"
            v-model="creditSelects.deadline"
          />
        </FieldSet>

        <StyledButton
          v-if="credit"
          :label="`${props.company.paymentMethods?.options.includes('credit') ? 'Actualizar datos' : 'Habilitar crédito'}`"
          :disabled="updateCompanyCreditLoading"
          class="bg-[#253C70] text-white ml-auto"
          @click="handleUpdate"
          :loading="updateCompanyCreditLoading"
        />

        <StyledButton
          v-if="!credit"
          :label="'Guardar'"
          :disabled="createCompanyCreditLoading"
          class="bg-[#253C70] text-white ml-auto"
          @click="handleCreate"
          :loading="createCompanyCreditLoading"
        />
      </template>

      <TransitionWrapper :show="!enabled">
        <div
          :class="[
            'rounded-md  mt-2 p-4',
            !enabled ? 'bg-red-50' : 'bg-blue-50',
          ]"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <InformationCircleIcon
                :class="[
                  'h-5 w-5 ' + (!enabled ? 'text-red-400' : 'text-blue-400'),
                ]"
                aria-hidden="true"
              />
            </div>
            <div class="ml-3 flex-1 md:flex md:justify-between">
              <p :class="[!enabled ? 'text-red-700' : 'text-blue-700']">
                {{ checkCreditIsActive }}
              </p>
            </div>
          </div>
        </div>
      </TransitionWrapper>
    </section>
  </RoundedBox>
  <LoadingModal :when="isLoading" />
</template>

<style scoped></style>
